import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Card, CircularProgress, Grid, Tab, Tabs, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { Prompt, useHistory, useLocation, useParams } from "react-router-dom";
import api from "../../../components/services/api";
import TruckTripTable from "../components/TruckTripTable";
import { errorText } from "../components/truckTripUtils";

const LEGS = [
  { key: "Inbound", label: "ขาไป" },
  { key: "Outbound", label: "ขากลับ" },
];

const tabSx = {
  "&.Mui-selected": {
    background: "#80C340",
    color: "white",
    borderTopLeftRadius: "15px",
    borderTopRightRadius: "15px",
  },
};

const LEAVE_MESSAGE = "มีข้อมูลเที่ยวรถที่ยังไม่บันทึก ต้องการออกจากหน้านี้?";

function initialLeg(search) {
  return new URLSearchParams(search).get("leg") === "Outbound" ? "Outbound" : "Inbound";
}

async function fetchTruckNos() {
  try {
    const res = await api.get("api/vehicle/list?status=1");
    const vehicles = Array.isArray(res?.data) ? res.data : [];
    const truckNos = vehicles.filter((x) => x.GroupId === "G001" && x.VehicleNo).map((x) => x.VehicleNo.trim());
    return [...new Set(truckNos)].sort((a, b) => a.localeCompare(b));
  } catch (e) {
    console.log(e);
    return [];
  }
}

export default function ViewTruckSchedule() {
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const MenuActive = useSelector((state) => state.MenuActive.List);
  const canEdit = MenuActive?.some((x) => x.ActionNo === "A033");

  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState(() => initialLeg(location.search));
  const [dirtyLegs, setDirtyLegs] = useState({});
  const [truckNos, setTruckNos] = useState([]);
  const anyDirty = Object.values(dirtyLegs).some(Boolean);

  useEffect(() => {
    api
      .get(`api/truck/detail/${id}`)
      .then((res) => setDetail(res.data))
      .catch((err) => setLoadError(errorText(err, "โหลดข้อมูลรอบรถไม่สำเร็จ")));
  }, [id]);

  useEffect(() => {
    fetchTruckNos().then(setTruckNos);
  }, []);

  // ปิดแท็บ / refresh ขณะมีข้อมูลค้าง (การเปลี่ยนหน้าในแอปใช้ <Prompt>)
  useEffect(() => {
    if (!anyDirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = LEAVE_MESSAGE;
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [anyDirty]);

  const handleDirtyChange = useCallback((legKey, dirty) => {
    setDirtyLegs((prev) => (prev[legKey] === dirty ? prev : { ...prev, [legKey]: dirty }));
  }, []);

  // อัปเดตเฉพาะขาที่บันทึก เพื่อไม่ล้างการแก้ไขที่ค้างอยู่ในอีกแท็บ
  const handleSaved = useCallback((legKey, trips) => {
    setDetail((prev) => ({ ...prev, [legKey]: { ...prev[legKey], Trips: trips } }));
  }, []);

  return (
    <div>
      <Prompt when={anyDirty} message={LEAVE_MESSAGE} />
      <Card variant="outlined" style={{ padding: "20px", margin: "25px" }}>
        <h6 style={{ marginTop: "10px" }} className="textcolorgreen">
          รายละเอียดรอบรถหัวลาก
        </h6>
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs>
            <Typography className="textcolordarkblue" variant="h5" component="h1">
              Truck Schedule Detail
            </Typography>
          </Grid>
          <Grid item sx={{ display: "flex", flexDirection: "row" }}>
            <Typography className="textcolorBule">Truck</Typography>
            <Typography>
              <span>&nbsp;</span> / Truck Schedule / {detail?.[tab]?.VoyNo ?? ""}
            </Typography>
          </Grid>
        </Grid>

        <Button startIcon={<ArrowBackIcon />} sx={{ marginTop: 2 }} onClick={() => history.push("/Truck/TruckSchedule")}>
          กลับไปตารางรอบรถ
        </Button>

        {loadError !== "" && (
          <Alert severity="error" sx={{ marginTop: 2 }}>
            {loadError}
          </Alert>
        )}
        {!detail && loadError === "" && (
          <Box sx={{ display: "flex", justifyContent: "center", padding: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {detail && (
          <Box sx={{ marginTop: 2 }}>
            <Tabs
              value={tab}
              onChange={(_e, value) => setTab(value)}
              TabIndicatorProps={{ style: { backgroundColor: "transparent" } }}
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              {LEGS.map(({ key, label }) => (
                <Tab key={key} value={key} sx={tabSx} label={`${label} ${detail[key].VoyNo} (${detail[key].Trips.length} เที่ยว)`} />
              ))}
            </Tabs>
            {/* render ทั้งสองขาไว้ตลอด เพื่อไม่ให้การแก้ไขที่ยังไม่บันทึกหายตอนสลับแท็บ */}
            {LEGS.map(({ key }) => (
              <Box key={key} hidden={tab !== key} sx={{ paddingTop: 2 }}>
                <TruckTripTable
                  legKey={key}
                  leg={detail[key]}
                  canEdit={canEdit}
                  truckNos={truckNos}
                  onSaved={handleSaved}
                  onDirtyChange={handleDirtyChange}
                />
              </Box>
            ))}
          </Box>
        )}
      </Card>
    </div>
  );
}
